import React, { memo, useEffect } from "react";
import { Card, CardBody, Row, Col, Button, UncontrolledAlert, ButtonToolbar, Spinner } from "reactstrap";
import { Field, reduxForm, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { MdArrowBack } from "react-icons/md";
import { Link } from "react-router-dom";
import { renderField } from "../../../../utils/renderfield";
import validate from "./validate";
import RolesSelect from "../../../Roles/RolesSelect";
import {
    resetRoles
} from "../../../Roles/actions/roles.actions";
import { resetPostUser } from "../../actions/user.actions";

import { accessControlFn } from "../../../../utils/accessControl";
import { permissionsConstants } from "../../../../constants/permission.constants";


const UserForm = memo((props) => {
    const { dispatch, handleSubmit, extDomain, permissions, external, reset, pristine, invalid, submitting, userId, postuser, currentUser, change } = props;

    const resetForm = () => {
        reset();
        accessControlFn(permissions, [permissionsConstants.ASSIGNUSER_ROLE], () => dispatch(resetRoles()));
    };

    useEffect(() => {
        dispatch(resetPostUser());

        return () => {
            dispatch(resetPostUser());
        };
    }, [dispatch]);

    return (
        <Col md={12} lg={12}>
            <Card>
                <CardBody>
                    {!external && (
                        <div className="card__title">
                            <h5 className="bold-text">
                                <Link to="/users" id="link-all-users">
                                    <MdArrowBack size={20} /> Back to users
                                </Link>
                            </h5>
                        </div>
                    )}
                    {external && (
                        <>
                            <div className="wizard">
                                <div className="wizard__steps">
                                    <div className="wizard__step">
                                        <p>Institution Details</p>
                                    </div>
                                    <div className="wizard__step wizard__step--active">
                                        <p>Create Admin User</p>
                                    </div>
                                    <div className="wizard__step">
                                        <p>Finish Setup</p>
                                    </div>
                                </div>
                            </div>
                            <br />
                        </>
                    )}
                    <div>
                        <form className="form" onSubmit={handleSubmit}>
                            {postuser && postuser.error && postuser.error.errors && postuser.error.errors.length ? (
                                <UncontrolledAlert color="danger">
                                    <h5 className="font-weight-bold">Please check the following fields for errors</h5>
                                    {postuser.error.errors.map((err) => (
                                        <p>
                                            <strong>{err.fieldName}:</strong> {err.message}
                                        </p>
                                    ))}
                                </UncontrolledAlert>
                            ) : postuser && postuser.error && postuser.error.message ? (
                                <UncontrolledAlert color="danger">
                                    <p>
                                        <strong>{postuser.error.message}</strong>
                                    </p>
                                </UncontrolledAlert>
                            ) : null}
                            <Row>
                                <Col lg="4">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label required">First name</span>
                                        <div className="form__form-group-field">
                                            <Field id="first_name" name="first_name" component={renderField} type="text" placeholder="first name" />
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label required">Last name</span>
                                        <div className="form__form-group-field">
                                            <Field id="last_name" name="last_name" component={renderField} type="text" placeholder="last name" />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg="4">
                                    <div className="form__form-group">
                                        <span className={userId ? "form__form-group-label" : "form__form-group-label required"}>Email address</span>
                                        <div className="form__form-group-field">
                                            <Field id="email" name="email" disabled={userId} component={renderField} type="email" placeholder="email address" />
                                        </div>
                                    </div>
                                </Col>
                                <Col lg="4">
                                    <div className="form__form-group">
                                        <span className="form__form-group-label required">Phone number</span>
                                        {!userId ? <small style={{ color: "blue", fontSize: "9px" }}>(e.g 08022012222)</small> : null}
                                        <div className="form__form-group-field">
                                            <Field id="phone_number" name="phone_number" component={renderField} type="tel" placeholder="phone number" />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            {!userId && (
                                <Row>
                                    <Col lg="4">
                                        <RolesSelect 
                                            required 
                                            id="role-select" 
                                            label="Assign roles to user" 
                                            createUserRole={true} 
                                            external={external}
                                            domainCode={external && extDomain ? extDomain.domainCode : currentUser.response.domainCode} 
                                            isCreateUserForm={true} 
                                            change={change}
                                        />
                                    </Col>
                                </Row>
                            )}

                            <ButtonToolbar className="form__button-toolbar">
                                <Button type="button" id="reset-form" onClick={resetForm} disabled={pristine || submitting}>
                                    Cancel
                                </Button>
                                <Button color="primary" id="submit-btn" type="submit" disabled={submitting || invalid}>
                                    {postuser && postuser.loading ? (
                                        <span>
                                            <Spinner size="sm" color="default" />{" "}
                                        </span>
                                    ) : null}
                                    Submit
                                </Button>
                            </ButtonToolbar>
                        </form>
                    </div>
                </CardBody>
            </Card>
        </Col>
    );
});

UserForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
};

const selector = formValueSelector("user_form");

export default reduxForm({
    form: "user_form",
    validate,
    enableReinitialize: true,
})(
    connect((state) => {
        const domain = selector(state, "domains");
        return {
            postuser: state.postuser,
            selectedDomainCode: domain?.code || null,
            permissions: state.permissions && state.permissions.response,
            currentUser: state.currentUser,
        };
    })(UserForm)
);
